import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../../hooks/useTranslations";
import { AdminWallMaterialFixing } from "../../../../../../../http/vertigrip/types/wallMaterialFixings";
import { useWallMaterialFixingsApi } from "../../../../../../../http/vertigrip/useWallMaterialFixings";
import { PaginationQuery } from "../../../../../../../types";
import SvgAdd from "../../../../../../icons/Add";
import Button from "../../../../../../ui/Button";
import FlexContent from "../../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../../ui/ModalDialog";
import SearchBar from "../../../../../../ui/SearchBar";
import Table from "../../../../../../ui/Table";
import Visibility from "../../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../../ui/VisibilityScope";

type Props = {
  active?: boolean;
};

const WallMaterialFixings: FC<Props> = ({ active }) => {
  const navigate = useNavigate();
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const { wallMaterialId } = useParams<{ wallMaterialId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "wallMaterialFixings" });
  const aborter = useRef(new AbortController());
  const wallMaterialFixingsApi = useWallMaterialFixingsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "wallMaterialFixings");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await wallMaterialFixingsApi.updateAdminWallMaterialFixingVisibility(id, { visibility }, { wallMaterialId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (wallMaterialFixingId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await wallMaterialFixingsApi.updateAdminWallMaterialFixingOrder(
        wallMaterialFixingId,
        { order: newOrder },
        { wallMaterialId },
      );
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<AdminWallMaterialFixing>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminWallMaterialFixing>(
    "vertigrip.wall_material_fixing",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = wallMaterialFixingsApi.useAdminWallMaterialFixings(
    wallMaterialId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.wallMaterialFixings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.wallMaterialFixings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button
          glyph={SvgAdd}
          buttonProps={{
            onClick: () =>
              navigate(
                `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}/fixings/new`,
              ),
          }}
        >
          {i18n.translation.wallMaterialFixings.createNew}
        </Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminWallMaterialFixing>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}/fixings/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) =>
          navigate(
            `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}/fixings/${item.id}`,
          )
        }
        rowActions={(item) => [
          getEditAction(i18n, () =>
            navigate(
              `/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${wallMaterialId}/fixings/${item.id}`,
            ),
          ),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.wallMaterialFixings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                wallMaterialFixingsApi
                  .deleteAdminWallMaterialFixing(itemToDelete.id, itemToDelete.wallMaterial.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.wallMaterialFixings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default WallMaterialFixings;
